import type { GpsReading } from './gpmf';

/** A GPS reading placed on the video's timeline (seconds). */
export interface TimedGps extends GpsReading {
  readonly time: number;
}

export interface TelemetryStats {
  /** m/s */
  readonly maxSpeed: number;
  /** Metres travelled. */
  readonly distance: number;
  /** Metres of total ascent. */
  readonly climb: number;
  /** Degrees, the larger of left and right. */
  readonly maxLean: number;
  /** Largest combined horizontal acceleration, in g. */
  readonly maxG: number;
  /** Share of the covered time with a usable GPS signal, 0..1. */
  readonly coverage: number;
}

/**
 * Ride data on a uniform grid (`rate` samples per second from `start`), so a
 * value at any video time is one interpolation away. `NaN` marks moments
 * without a GPS signal. Positions exist only to find private places on the
 * route; nothing draws or exports them.
 */
export interface Telemetry {
  readonly rate: number;
  readonly start: number;
  /** Degrees (float64: float32 would round positions to metres). */
  readonly latitude: Float64Array;
  readonly longitude: Float64Array;
  /** m/s */
  readonly speed: Float32Array;
  /** Metres. */
  readonly altitude: Float32Array;
  /** Estimated lean angle in degrees; positive leans right. */
  readonly lean: Float32Array;
  /** Acceleration in g: forward (+) / braking (−)… */
  readonly gForward: Float32Array;
  /** …and sideways, positive to the right. */
  readonly gSide: Float32Array;
  readonly stats: TelemetryStats;
}

/** Values at one moment of the video. */
export interface TelemetryReading {
  readonly speed: number;
  readonly altitude: number;
  readonly lean: number;
  readonly gForward: number;
  readonly gSide: number;
}

export const TELEMETRY_RATE = 10;
const G = 9.80665;
const EARTH_RADIUS = 6_371_000;
/** Longer gaps without a reading are shown as "no signal" instead of being bridged. */
const MAX_GAP_SEC = 2;
/** Below this the heading is noise, so sideways forces are not estimated. */
const MIN_TURN_SPEED = 3;
const MAX_LEAN_DEG = 65;
const MAX_G = 1.5;
/** Altitude changes smaller than this are GPS noise, not climbing. */
const CLIMB_HYSTERESIS_M = 3;

/** Centred moving average that ignores missing values and keeps gaps as gaps. */
function smooth(values: Float32Array, radius: number): Float32Array {
  const out = new Float32Array(values.length);
  for (let i = 0; i < values.length; i++) {
    if (Number.isNaN(values[i]!)) {
      out[i] = Number.NaN;
      continue;
    }
    let sum = 0;
    let count = 0;
    for (let k = Math.max(0, i - radius); k <= Math.min(values.length - 1, i + radius); k++) {
      const value = values[k]!;
      if (!Number.isNaN(value)) {
        sum += value;
        count++;
      }
    }
    out[i] = sum / count;
  }
  return out;
}

/** Central difference per second; NaN where either neighbour is missing. */
function derivative(values: Float32Array, radius: number, rate: number): Float32Array {
  const out = new Float32Array(values.length).fill(Number.NaN);
  for (let i = 0; i < values.length; i++) {
    const a = Math.max(0, i - radius);
    const b = Math.min(values.length - 1, i + radius);
    if (b > a) out[i] = ((values[b]! - values[a]!) * rate) / (b - a);
  }
  return out;
}

const clamp = (value: number, limit: number) => Math.max(-limit, Math.min(limit, value));

/** Resamples readings onto the grid, leaving long gaps empty. */
function resample(readings: readonly TimedGps[], start: number, length: number, rate: number) {
  const lat = new Float32Array(length).fill(Number.NaN);
  const lon = new Float32Array(length).fill(Number.NaN);
  const latitude = new Float64Array(length).fill(Number.NaN);
  const longitude = new Float64Array(length).fill(Number.NaN);
  const alt = new Float32Array(length).fill(Number.NaN);
  const speed = new Float32Array(length).fill(Number.NaN);
  const origin = readings[0]!;
  const metresPerDegLat = (Math.PI / 180) * EARTH_RADIUS;
  const metresPerDegLon = metresPerDegLat * Math.cos((origin.latitude * Math.PI) / 180);
  let j = 0;
  for (let i = 0; i < length; i++) {
    const t = start + i / rate;
    while (j + 1 < readings.length && readings[j + 1]!.time <= t) j++;
    const a = readings[j]!;
    const b = readings[j + 1];
    if (!b) {
      if (Math.abs(t - a.time) > 1 / rate) continue;
    } else if (b.time - a.time > MAX_GAP_SEC) {
      continue;
    }
    const f = b && b.time > a.time ? Math.min(1, Math.max(0, (t - a.time) / (b.time - a.time))) : 0;
    const mix = (x: number, y: number | undefined) => (y === undefined ? x : x + (y - x) * f);
    // Local metres east/north of the first reading: float32-safe and flat-earth accurate over a ride.
    latitude[i] = mix(a.latitude, b?.latitude);
    longitude[i] = mix(a.longitude, b?.longitude);
    lon[i] = (longitude[i]! - origin.longitude) * metresPerDegLon;
    lat[i] = (latitude[i]! - origin.latitude) * metresPerDegLat;
    alt[i] = mix(a.altitude, b?.altitude);
    speed[i] = mix(a.speed, b?.speed);
  }
  return { east: lon, north: lat, alt, speed, latitude, longitude };
}

function computeStats(telemetry: Omit<Telemetry, 'stats'>): TelemetryStats {
  let maxSpeed = 0;
  let distance = 0;
  let climb = 0;
  let maxLean = 0;
  let maxG = 0;
  let covered = 0;
  let base: number | null = null;
  for (let i = 0; i < telemetry.speed.length; i++) {
    const speed = telemetry.speed[i]!;
    if (Number.isNaN(speed)) continue;
    covered++;
    maxSpeed = Math.max(maxSpeed, speed);
    distance += speed / telemetry.rate;
    maxLean = Math.max(maxLean, Math.abs(telemetry.lean[i]!));
    maxG = Math.max(maxG, Math.hypot(telemetry.gForward[i]!, telemetry.gSide[i]!));
    const altitude = telemetry.altitude[i]!;
    if (base === null || altitude < base) base = altitude;
    else if (altitude - base >= CLIMB_HYSTERESIS_M) {
      climb += altitude - base;
      base = altitude;
    }
  }
  return { maxSpeed, distance, climb, maxLean, maxG, coverage: telemetry.speed.length ? covered / telemetry.speed.length : 0 };
}

/**
 * Turns raw GPS readings into the overlay's data. Speed comes from the GPS
 * Doppler value; forward force from its change over time; sideways force from
 * how fast the direction of travel turns (a = v·ω, with ω from the cross
 * product of velocity and acceleration so no angle unwrapping is needed); and
 * lean from the balance of a motorcycle in a steady turn, tan(lean) = a/g.
 * Accelerometers are not used: engine vibration swamps them.
 */
export function deriveTelemetry(input: readonly TimedGps[], rate = TELEMETRY_RATE): Telemetry | null {
  const readings = input
    .filter((r) => Number.isFinite(r.time))
    .slice()
    .sort((a, b) => a.time - b.time)
    .filter((r, i, all) => i === 0 || r.time > all[i - 1]!.time);
  if (readings.length < 2) return null;
  const start = readings[0]!.time;
  const length = Math.floor((readings.at(-1)!.time - start) * rate) + 1;
  if (length < 2) return null;

  const raw = resample(readings, start, length, rate);
  const second = Math.round(rate);
  const speed = smooth(raw.speed, Math.round(rate * 0.25));
  const east = smooth(raw.east, Math.round(rate * 0.5));
  const north = smooth(raw.north, Math.round(rate * 0.5));
  const ve = smooth(derivative(east, Math.round(rate * 0.5), rate), Math.round(rate * 0.3));
  const vn = smooth(derivative(north, Math.round(rate * 0.5), rate), Math.round(rate * 0.3));
  const ae = derivative(ve, Math.round(rate * 0.5), rate);
  const an = derivative(vn, Math.round(rate * 0.5), rate);
  const forward = derivative(smooth(raw.speed, second), Math.round(rate * 0.5), rate);

  const gForward = new Float32Array(length);
  const gSideRaw = new Float32Array(length);
  for (let i = 0; i < length; i++) {
    if (Number.isNaN(speed[i]!)) {
      gForward[i] = Number.NaN;
      gSideRaw[i] = Number.NaN;
      continue;
    }
    const v2 = ve[i]! ** 2 + vn[i]! ** 2;
    gForward[i] = Number.isNaN(forward[i]!) ? 0 : clamp(forward[i]! / G, MAX_G);
    // Turn rate from the track (velocity × acceleration / |v|², negative turning right in
    // east/north axes), times the Doppler speed. Gating on the Doppler speed keeps position
    // jitter at walking pace from turning into phantom lean.
    const turnRate = (ve[i]! * an[i]! - vn[i]! * ae[i]!) / v2;
    gSideRaw[i] = speed[i]! >= MIN_TURN_SPEED && v2 > 1 && Number.isFinite(turnRate) ? clamp((-turnRate * speed[i]!) / G, MAX_G) : 0;
  }
  const gSide = smooth(gSideRaw, Math.round(rate * 0.3));
  const lean = new Float32Array(length);
  for (let i = 0; i < length; i++) {
    lean[i] = Number.isNaN(gSide[i]!) ? Number.NaN : clamp((Math.atan(gSide[i]!) * 180) / Math.PI, MAX_LEAN_DEG);
  }

  const series = { rate, start, latitude: raw.latitude, longitude: raw.longitude, speed, altitude: smooth(raw.alt, second * 2), lean, gForward: smooth(gForward, Math.round(rate * 0.3)), gSide };
  return { ...series, stats: computeStats(series) };
}

/** Shifts each part's readings onto one timeline (chapters of a joined recording). */
export const concatReadings = (parts: ReadonlyArray<{ readonly readings: readonly TimedGps[]; readonly offset: number }>): TimedGps[] =>
  parts.flatMap(({ readings, offset }) => readings.map((r) => ({ ...r, time: r.time + offset })));

/**
 * Values at a video time, linearly interpolated. Null outside the recorded
 * span; within it, missing signal shows up as NaN values.
 */
export function telemetryAt(telemetry: Telemetry, time: number): TelemetryReading | null {
  const position = (time - telemetry.start) * telemetry.rate;
  const last = telemetry.speed.length - 1;
  if (!(position >= -0.5) || position > last + 0.5) return null;
  const clamped = Math.min(last, Math.max(0, position));
  const i = Math.floor(clamped);
  const j = Math.min(last, i + 1);
  const f = clamped - i;
  const at = (series: Float32Array) => series[i]! + (series[j]! - series[i]!) * f;
  // Across the edge of a gap, show the side we are on instead of NaN-poisoning both.
  const pick = (series: Float32Array) => (Number.isNaN(series[j]!) ? series[i]! : Number.isNaN(series[i]!) ? series[j]! : at(series));
  return {
    speed: pick(telemetry.speed),
    altitude: pick(telemetry.altitude),
    lean: pick(telemetry.lean),
    gForward: pick(telemetry.gForward),
    gSide: pick(telemetry.gSide),
  };
}
